// TimeseriesChart — minimal multi-series SVG line chart for the creator
// analytics dashboard. No charting library so the bundle stays small.
import React, { useMemo, useState } from 'react';
import { B } from '../constants';

const COLORS = [B.forest, B.amber, B.sky, B.mint];

export default function TimeseriesChart({ series, metrics, height = 240 }) {
  const [hoverIdx, setHoverIdx] = useState(null);

  const layout = useMemo(() => {
    if (!series || series.length === 0) return null;
    const PAD = { top: 16, right: 16, bottom: 28, left: 44 };
    const W = 640;
    const H = height;
    const innerW = W - PAD.left - PAD.right;
    const innerH = H - PAD.top - PAD.bottom;
    const n = series.length;

    let maxY = 0;
    for (const row of series) {
      for (const m of metrics) {
        const v = row[m.key] || 0;
        if (v > maxY) maxY = v;
      }
    }
    if (maxY === 0) maxY = 1;

    const x = (i) => PAD.left + (n === 1 ? innerW / 2 : (innerW * i) / (n - 1));
    const y = (v) => PAD.top + innerH - (innerH * v) / maxY;

    const paths = metrics.map((m, mi) => ({
      ...m,
      color: m.color || COLORS[mi % COLORS.length],
      d: series.map((row, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(row[m.key] || 0)}`).join(' '),
    }));

    // Tick labels: first, middle, last (avoid overlap on narrow screens).
    const labelIdx = n <= 2 ? series.map((_, i) => i) : [0, Math.floor(n / 2), n - 1];

    return { W, H, PAD, innerW, innerH, maxY, x, y, paths, labelIdx, n };
  }, [series, metrics, height]);

  if (!layout) {
    return (
      <div style={{
        height, display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: B.muted, background: '#fff', borderRadius: 12,
      }}>
        No data yet.
      </div>
    );
  }

  const { W, H, PAD, paths, labelIdx, maxY, x, y } = layout;

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="none" role="img" aria-label="Time series">
        {/* Y-axis baseline */}
        {[0, 0.25, 0.5, 0.75, 1].map((t) => (
          <line
            key={t}
            x1={PAD.left} x2={W - PAD.right}
            y1={PAD.top + (H - PAD.top - PAD.bottom) * t}
            y2={PAD.top + (H - PAD.top - PAD.bottom) * t}
            stroke="#f0eee8" strokeDasharray={t === 1 ? '0' : '3 3'}
          />
        ))}
        {/* Y labels */}
        {[0, 0.5, 1].map((t) => (
          <text
            key={t}
            x={PAD.left - 6}
            y={PAD.top + (H - PAD.top - PAD.bottom) * (1 - t) + 4}
            textAnchor="end" fontSize="11" fill={B.muted}
          >
            {Math.round(maxY * t)}
          </text>
        ))}
        {/* X labels */}
        {labelIdx.map((i) => (
          <text
            key={i}
            x={x(i)} y={H - 8}
            textAnchor="middle" fontSize="11" fill={B.muted}
          >
            {series[i].date.slice(5)}
          </text>
        ))}
        {/* Series */}
        {paths.map((p) => (
          <path key={p.key} d={p.d} fill="none" stroke={p.color} strokeWidth={2} />
        ))}
        {/* Hover dots */}
        {hoverIdx !== null && paths.map((p) => (
          <circle
            key={p.key}
            cx={x(hoverIdx)}
            cy={y(series[hoverIdx][p.key] || 0)}
            r={3} fill={p.color}
          />
        ))}
        {/* Hit area */}
        <rect
          x={PAD.left} y={PAD.top}
          width={W - PAD.left - PAD.right} height={H - PAD.top - PAD.bottom}
          fill="transparent"
          onMouseMove={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            const ratio = (e.clientX - r.left) / r.width;
            const i = Math.max(0, Math.min(series.length - 1, Math.round(ratio * (series.length - 1))));
            setHoverIdx(i);
          }}
          onMouseLeave={() => setHoverIdx(null)}
        />
      </svg>

      {/* Legend + hover readout */}
      <div style={{
        display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8, padding: '0 4px',
        fontSize: 12, color: B.muted, fontFamily: "'Montserrat',sans-serif",
      }}>
        {paths.map((p) => (
          <span key={p.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 10, height: 10, background: p.color, borderRadius: 2, display: 'inline-block' }} />
            {p.label}
            {hoverIdx !== null && (
              <strong style={{ color: B.night, marginLeft: 4 }}>
                {series[hoverIdx][p.key] || 0}
              </strong>
            )}
          </span>
        ))}
        {hoverIdx !== null && (
          <span style={{ marginLeft: 'auto', color: B.night }}>{series[hoverIdx].date}</span>
        )}
      </div>
    </div>
  );
}
